import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Input, Segmented, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CutJobClient, type CutJobDTO, type PanelRef } from "../../../api/cutJobs";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import { OpenRecord } from "../../../components/OpenRecord";
import { NexpimView } from "../../../views";
import { CandidateTable } from "./CandidateTable";
import { CUT_JOBS_KEY, cutJobStatusLabel, cutJobStatusVariant, JOB_PROCESSES, jobProcessLabel, type JobProcess } from "./cutJobModel";

type View = "jobs" | "waiting";

/**
 * Cut jobs (docs/cutting-jobs §6): every job with its progress, and the lots
 * whose production BOM is confirmed with raw stock in no job yet. Tick what
 * Cut Rite batched (a bed's Noir with a nightstand's), give Cut Rite's number
 * if there is one, create the job; confirm it from its page.
 */
export function CutJobsPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const allowed = hasView(NexpimView.PRODUCTION_ORDERS);
  const [view, setView] = useState<View>("jobs");
  const [process, setProcess] = useState<JobProcess>("CUT");
  const [finished, setFinished] = useState(false);
  const [selected, setSelected] = useState<PanelRef[]>([]);
  const [cutrite, setCutrite] = useState("");
  const jobs = useQuery({ queryKey: [...CUT_JOBS_KEY, "list"], queryFn: () => client.findAll(), enabled: allowed });
  const create = useMutation({
    mutationFn: () => client.createJob(selected, cutrite.trim() || null, process),
    onSuccess: async (detail) => {
      setSelected([]);
      setCutrite("");
      await queryClient.invalidateQueries({ queryKey: CUT_JOBS_KEY });
      void navigate({ to: "/production/cut-jobs/$jobId", params: { jobId: String(detail.job.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const rows = (jobs.data ?? []).filter((job) => job.process_code === process && (finished || (job.status !== "DONE" && job.status !== "CANCELLED")));
  // The saw's order is the list's: drag the open jobs (docs/cutting-jobs §9).
  const reorder = useMutation({
    mutationFn: (ordered: CutJobDTO[]) => client.reorder(ordered.filter((job) => job.status !== "DONE" && job.status !== "CANCELLED").map((job) => job.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUT_JOBS_KEY }),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const title = t("navigation.item.cut-jobs", "Cut jobs");

  const columns = useMemo(() => {
    const c = defineColumns<CutJobDTO>();
    return [
      c.number("place", (row) => row.position ?? 0, { header: t("cut-job.place", "#"), priority: 1, enableSorting: false }),
      c.number("number", (row) => row.number, { header: t("cut-job.number", "Job"), priority: 1, enableSorting: false }),
      c.text("cutrite_number", (row) => row.cutrite_number ?? "", { header: t("cut-job.cutrite", "Cut Rite number"), priority: 1 }),
      c.custom("status", (row) => cutJobStatusLabel(row.status, t), (row) => (
        <Badge size="sm" variant={cutJobStatusVariant(row.status)}>{cutJobStatusLabel(row.status, t)}</Badge>
      ), { header: t("column.status", "Status"), filter: "select", priority: 1 }),
      c.text("lots", (row) => row.lots ?? "", { header: t("cut-job.lots", "Lots"), priority: 1 }),
      c.text("panels", (row) => row.panels ?? "", { header: t("cut-job.panels", "Raw panels"), priority: 2 }),
      c.text("sheets", (row) => `${formatNumber(row.sheets_used ?? 0)}/${formatNumber(row.sheets_planned ?? 0)}`, {
        header: t("cut-job.sheets", "Sheets used / planned"),
        priority: 2,
      }),
      c.text("pieces", (row) => `${formatNumber(row.pieces_done ?? 0)}/${formatNumber(row.pieces_required ?? 0)}`, {
        header: t("cut-job.pieces-progress", "Pieces cut"),
        priority: 2,
      }),
      c.text("due_date", (row) => row.due_date ?? "", { header: t("cut-job.due", "Due"), priority: 2 }),
      c.number("document_count", (row) => row.document_count ?? 0, { header: t("cut-job.documents", "Documents"), priority: 3 }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <p className="p-4 text-[13px] text-ink-muted lg:p-6">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "cut-job.page.subtitle",
          "The saw works from cut jobs: the lots Cut Rite batched, cut as one. A lot is released once all its raw panels are in confirmed jobs.",
        )}
      />
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line px-4 py-2 lg:px-6">
        <Segmented<JobProcess>
          value={process}
          onChange={(next) => {
            setProcess(next);
            setSelected([]);
          }}
          options={JOB_PROCESSES.map((code) => ({ value: code, label: jobProcessLabel(code, t) }))}
        />
        <Segmented<View>
          value={view}
          onChange={setView}
          options={[
            { value: "jobs", label: t("cut-job.view.jobs", "Jobs") },
            { value: "waiting", label: t("cut-job.view.waiting", "Lots waiting") },
          ]}
        />
        {view === "jobs" && <Switch checked={finished} onChange={setFinished} label={t("cut-job.show-finished", "Show done and cancelled")} />}
      </div>
      {view === "jobs" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <DataTable<CutJobDTO>
            data={rows}
            columns={columns}
            loading={jobs.isLoading}
            getRowId={(row) => String(row.id)}
            stateKey="production.cut-jobs"
            onRowClick={(row) => void navigate({ to: "/production/cut-jobs/$jobId", params: { jobId: String(row.id) } })}
            onReorder={canWrite && !finished ? (ordered) => reorder.mutate(ordered) : undefined}
            toolbarLead={canWrite && !finished ? (
              <span className="text-[12px] text-ink-muted">{t("cut-job.drag-hint", "Drag the jobs into the order the saw cuts them.")}</span>
            ) : undefined}
            rowActions={(row) => <OpenRecord target={{ kind: "cut-job", id: row.id }} label={String(row.number)} />}
            emptyMessage={t("cut-job.empty", "No cut job open. Create one from the lots waiting.")}
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-4 lg:p-6">
          <div className="flex min-h-[50vh] flex-1 flex-col">
            <CandidateTable key={process} processCode={process} onSelect={setSelected} />
          </div>
          {canWrite && (
            <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-line bg-surface py-2">
              <Input
                className="max-w-56"
                placeholder={t("cut-job.cutrite.placeholder", "Cut Rite number (optional)")}
                value={cutrite}
                onChange={(event) => setCutrite(event.target.value)}
              />
              <Button variant="primary" loading={create.isPending} disabled={selected.length === 0} onClick={() => create.mutate()}>
                {t("cut-job.create", "Create the job")}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
